import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDate, formatDateTime, amountToWords } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { InfoRequestPanel } from "@/components/InfoRequestPanel";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Pencil, Printer, BadgeDollarSign, Unlock, Trash2, Loader2, Building2, Send, XCircle, Check, GitBranch, FileText, CheckCircle2, Clock, Paperclip, Upload, MessageSquareMore,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { stagesFor, STAGE_ROLE, ADVANCE_LABEL, STEP_LABEL, canAct, STATUS_META, PAYMENT_METHOD_LABEL, PAYMENT_TYPE_LABEL } from "@/lib/workflow";

function TinyPill({ s }) {
  const meta = STATUS_META[s] || { label: s, badge: "bg-slate-100 text-slate-700 border-slate-300" };
  return <span className={cn("text-[11px] px-2 py-0.5 rounded-md border font-medium whitespace-nowrap", meta.badge)}>{meta.label}</span>;
}

function activityIcon(action) {
  const a = (action || "").toLowerCase();
  if (a.startsWith("created")) return { Icon: FileText, cls: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400" };
  if (a.startsWith("submitted")) return { Icon: Send, cls: "bg-blue-100 text-blue-600 dark:bg-blue-950/50 dark:text-blue-400" };
  if (a.startsWith("additional info")) return { Icon: MessageSquareMore, cls: "bg-amber-100 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400" };
  if (a.startsWith("rejected")) return { Icon: XCircle, cls: "bg-rose-100 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400" };
  if (a.includes("paid")) return { Icon: BadgeDollarSign, cls: "bg-emerald-100 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400" };
  if (a.includes("audit")) return { Icon: Clock, cls: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400" };
  if (a.includes("unlock")) return { Icon: Unlock, cls: "bg-amber-100 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400" };
  if (a.includes("updated")) return { Icon: Pencil, cls: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400" };
  return { Icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400" };
}

export default function VoucherDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const location = useLocation();
  const [v, setV] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoNote, setInfoNote] = useState("");
  const [userMap, setUserMap] = useState({});
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ payment_date: "", payment_reference: "", notes: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/vouchers/${id}`);
      setV(data);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/vouchers");
      return;
    }
    api.get(`/vouchers/${id}/activity`).then(({ data }) => setAudit(data)).catch(() => {});
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/users/assignable").then(({ data }) => {
      const m = {};
      data.forEach((u) => { m[u.id] = u.name; });
      setUserMap(m);
    }).catch(() => {});
  }, []);

  const action = async (path, body, msg) => {
    setBusy(true);
    try {
      await api.post(`/vouchers/${id}/${path}`, body || {});
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doUnlock = async () => {
    if (!unlockReason.trim()) return toast.error("A reason is required to unlock");
    await action("unlock", { notes: unlockReason.trim() }, "Voucher unlocked");
    setUnlockOpen(false);
    setUnlockReason("");
  };

  const doReject = async () => {
    if (!rejectReason.trim()) return toast.error("A reason is required to reject");
    await action("reject", { notes: rejectReason.trim() }, "Voucher returned to creator");
    setRejectOpen(false);
    setRejectReason("");
  };

  const doPay = async () => {
    await action("pay", payForm, "Marked as Paid & locked");
    setPayOpen(false);
    setPayForm({ payment_date: "", payment_reference: "", notes: "" });
  };

  const doRequestInfo = async () => {
    if (!infoNote.trim()) return toast.error("Please describe the information you need");
    await action("request-info", { notes: infoNote.trim() }, "Returned to creator for additional info");
    setInfoOpen(false);
    setInfoNote("");
  };

  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/vouchers/${id}`);
      toast.success("Voucher deleted");
      navigate("/vouchers");
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  const printPdf = () => window.open(`${API}/vouchers/${id}/pdf`, "_blank");

  const uploadFile = async (kind, fileList) => {
    const f = fileList?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      await api.post(`/vouchers/${id}/attachments?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attachment uploaded");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  const delAtt = async (kind, attId) => {
    try {
      await api.delete(`/vouchers/${id}/attachments/${attId}?kind=${kind}`);
      toast.success("Attachment removed");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const openAtt = (attId) => window.open(`${API}/vouchers/${id}/attachments/${attId}/download`, "_blank");

  if (!v) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const canEdit = !v.locked && v.status === "draft" && canAct(user, "draft");
  const isActionable = Boolean(STAGE_ROLE[v.status]);
  const stageAssignee = (v.workflow_assignees || {})[v.status];
  const isStageAssignee = isAdmin || !stageAssignee || stageAssignee === user?.id;
  const userCanAct = canAct(user, v.status) && isStageAssignee && (v.status !== "draft" || isAdmin || v.created_by === user?.id);
  const showAdvance = isActionable && userCanAct;
  const showReject = isActionable && v.status !== "draft" && userCanAct;
  const advanceLabel = ADVANCE_LABEL[v.status];
  const seq = stagesFor(v);
  const curIdx = v.status === "paid" ? seq.length : seq.indexOf(v.status === "info_requested" ? v.info_return_stage : v.status);
  const canRespondInfo = v.status === "info_requested" && (isAdmin || v.created_by === user?.id);
  const signStages = seq.filter((s) => s !== "unpaid");
  const canPay = v.status === "unpaid" && (isAdmin || (user?.role === "creator" && v.created_by === user?.id));
  const signerByStage = {};
  audit.forEach((a) => {
    if (a.from_status && a.to_status && !["draft", "info_requested"].includes(a.to_status) && a.from_status !== "info_requested") signerByStage[a.from_status] = a.user_name;
  });
  const canCreatorAtt = isAdmin || (user?.role === "creator" && v.created_by === user?.id && !v.locked && v.status === "draft");
  const accAssignee = (v.workflow_assignees || {}).accounts;
  const canAccountsAtt = isAdmin || (user?.role === "accounts" && v.status !== "draft" && (v.workflow_stages || []).includes("accounts") && (accAssignee === user?.id || (!accAssignee && !(v.cc_users || []).includes(user?.id))));
  const canAdditionalAtt = isAdmin || (user?.role === "creator" && v.created_by === user?.id && !v.locked && !["draft", "unpaid", "paid"].includes(v.status));
  const attachSections = [
    { kind: "creator", title: "Attachments", list: v.attachments || [], can: canCreatorAtt, hint: "Uploaded by the creator" },
    { kind: "accounts", title: "Accounts Attachment", list: v.accounts_attachments || [], can: canAccountsAtt, hint: "Accounts team only" },
    { kind: "additional", title: "Additional Attachment", list: v.additional_attachments || [], can: canAdditionalAtt, hint: "Added by the creator after submitting · visible to everyone" },
  ];
  const money = (n) => formatMoney(n, v.currency);

  return (
    <div className="space-y-6" data-testid="voucher-detail-page">
      {/* Header actions */}
      <div className="no-print flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(location.state?.from || "/vouchers")} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2.5">
              <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight font-mono">{v.voucher_no}</h2>
              <StatusBadge status={v.status} locked={v.locked} />
            </div>
            <p className="text-sm text-slate-500">Created {formatDateTime(v.created_at)}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2" onClick={printPdf} data-testid="print-pdf-btn">
            <Printer className="w-4 h-4" /> Print / PDF
          </Button>
          {canEdit && (
            <Button variant="outline" className="gap-2" onClick={() => navigate(`/vouchers/${id}/edit`)} data-testid="edit-btn">
              <Pencil className="w-4 h-4" /> Edit
            </Button>
          )}
          {isAdmin && v.status === "draft" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="icon" data-testid="delete-btn"><Trash2 className="w-4 h-4 text-rose-500" /></Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this draft?</AlertDialogTitle>
                  <AlertDialogDescription>This permanently deletes the draft voucher {v.voucher_no}.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={del} className="bg-rose-600 hover:bg-rose-500" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {showReject && (
            <Button variant="outline" className="gap-2 border-amber-300 text-amber-700 dark:text-amber-300" disabled={busy}
              onClick={() => setInfoOpen(true)} data-testid="request-info-btn">
              <MessageSquareMore className="w-4 h-4" /> Additional Info
            </Button>
          )}
          {showReject && (
            <Button variant="outline" className="gap-2 border-rose-300 text-rose-600 dark:text-rose-300" disabled={busy}
              onClick={() => setRejectOpen(true)} data-testid="reject-btn">
              <XCircle className="w-4 h-4" /> Reject
            </Button>
          )}
          {showAdvance && (
            <Button className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground" disabled={busy}
              onClick={() => action("advance", {}, "Voucher moved to the next stage")} data-testid="advance-btn">
              {v.status === "draft" ? <Send className="w-4 h-4" /> : <Check className="w-4 h-4" />} {advanceLabel}
            </Button>
          )}
          {canPay && (
            <Button className="gap-2 bg-emerald-600 hover:bg-emerald-500 text-white" disabled={busy}
              onClick={() => setPayOpen(true)} data-testid="pay-btn">
              <BadgeDollarSign className="w-4 h-4" /> Mark as Paid
            </Button>
          )}
          {isAdmin && v.locked && (
            <Button variant="outline" className="gap-2 border-amber-300 text-amber-700 dark:text-amber-300"
              onClick={() => setUnlockOpen(true)} data-testid="unlock-btn">
              <Unlock className="w-4 h-4" /> Unlock
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 space-y-6 min-w-0">
      {/* Workflow stepper */}
      <Card className="no-print p-5 border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-slate-500 mb-4">
          <GitBranch className="w-3.5 h-3.5" /> Approval Workflow
          {v.voucher_type === "HR" && <span className="text-violet-600 dark:text-violet-400 normal-case tracking-normal">· HR fast-track</span>}
        </div>
        <div className="flex items-center overflow-x-auto pb-1">
          {seq.map((s, i) => {
            const done = i < curIdx;
            const active = i === curIdx;
            const aid = (v.workflow_assignees || {})[s];
            const stageUser = s === "draft" ? v.created_by_name : (aid ? userMap[aid] : "");
            return (
              <div key={s} className="flex items-center shrink-0">
                <div className="flex flex-col items-center gap-1.5" data-testid={`step-${s}`}>
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border-2",
                    done ? "bg-emerald-500 border-emerald-500 text-white"
                      : active ? "bg-primary border-primary text-primary-foreground"
                      : "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-400"
                  )}>
                    {done ? <Check className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className={cn("text-[11px] whitespace-nowrap", active ? "font-semibold text-slate-900 dark:text-slate-100" : "text-slate-500")}>
                    {s === "unpaid" && v.status === "paid" ? "Paid" : STEP_LABEL[s]}
                  </span>
                  {stageUser ? (
                    <span className="text-[10px] text-slate-400 whitespace-nowrap max-w-[90px] truncate" data-testid={`step-user-${s}`}>{stageUser}</span>
                  ) : null}
                </div>
                {i < seq.length - 1 && (
                  <div className={cn("h-0.5 w-8 md:w-14 mx-1 mb-5", i < curIdx ? "bg-emerald-500" : "bg-slate-200 dark:bg-slate-700")} />
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {v.status === "info_requested" && (
        <InfoRequestPanel key={v.updated_at} v={v} canRespond={canRespondInfo} busy={busy}
          onSubmit={(text) => action("submit-info", { additional_info: text }, "Additional info submitted")} />
      )}

      {v.status === "draft" && v.rejection_reason && (
        <div className="no-print flex items-start gap-2 text-sm bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900 rounded-lg px-4 py-3" data-testid="rejection-banner">
          <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span><strong>Returned to Creator.</strong> {v.rejection_reason}{v.rejected_by ? ` — by ${v.rejected_by}` : ""}</span>
        </div>
      )}

      {v.locked && (
        <div className="no-print flex items-center gap-2 text-sm bg-slate-900 text-slate-100 dark:bg-slate-100 dark:text-slate-900 rounded-lg px-4 py-3">
          <Unlock className="w-4 h-4" />
          This voucher is <strong>Paid &amp; locked</strong> for auditability. Admin must unlock (with a reason) to make changes.
        </div>
      )}

      {/* Printable voucher */}
      <Card className="print-area p-6 md:p-8 border-slate-200 dark:border-slate-800">
        <div className="flex flex-wrap justify-between gap-4 pb-5 border-b-2 border-slate-900 dark:border-slate-100">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Payment Voucher</div>
            <div className="font-mono text-2xl font-bold mt-1">{v.voucher_no}</div>
            {v.company_name && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full bg-accent text-accent-foreground border border-primary/20" data-testid="detail-company">
                  {v.company_name}
                </span>
                {v.voucher_type && (
                  <span className="inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700" data-testid="detail-voucher-type">
                    {v.voucher_type}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="text-right text-sm space-y-2">
            <div>
              <div className="text-slate-500">Date</div>
              <div className="font-mono font-medium">{formatDate(v.date)}</div>
            </div>
            <div>
              <div className="text-slate-500">Payment</div>
              <div className="font-medium">{PAYMENT_METHOD_LABEL[v.payment_method] || v.payment_method} · {PAYMENT_TYPE_LABEL[v.payment_type] || v.payment_type}</div>
            </div>
            <div>
              <div className="text-slate-500">Currency</div>
              <div className="font-medium">{v.currency || "AED"}</div>
            </div>
            {(v.po_number || v.po_amount != null) && (
              <div>
                <div className="text-slate-500">PO</div>
                <div className="font-medium">{v.po_number || "—"}{v.po_amount != null ? ` · ${money(v.po_amount)}` : ""}</div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-5">
          <div className="bg-slate-50 dark:bg-slate-800/40 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1.5 mb-1.5">
              <Building2 className="w-3.5 h-3.5" /> Pay to / Payee
            </div>
            <div className="font-semibold">{v.vendor_name}</div>
            {v.vendor_trn && <div className="text-sm text-slate-500 font-mono mt-0.5">TRN: {v.vendor_trn}</div>}
          </div>
          <div className="text-sm space-y-1 md:text-right">
            {v.reference && (<div><span className="text-slate-400">Reference: </span><span className="font-medium">{v.reference}</span></div>)}
            {v.approved_by_name && (<div><span className="text-slate-400">Approved by: </span><span className="font-medium">{v.approved_by_name}</span></div>)}
            {v.paid_by_name && (<div><span className="text-slate-400">Paid by: </span><span className="font-medium">{v.paid_by_name}</span></div>)}
            {v.payment_date && (<div data-testid="payment-date-display"><span className="text-slate-400">Payment date: </span><span className="font-medium">{formatDate(v.payment_date)}</span></div>)}
            {v.payment_reference && (<div data-testid="payment-ref-display"><span className="text-slate-400">Payment ref: </span><span className="font-medium">{v.payment_reference}</span></div>)}
            <div><span className="text-slate-400">Prepared by: </span><span className="font-medium">{v.created_by_name}</span></div>
            {(v.cc_names || []).length > 0 && (<div data-testid="cc-display"><span className="text-slate-400">CC: </span><span className="font-medium">{v.cc_names.join(", ")}</span></div>)}
            {(v.cc_users || []).includes(user?.id) && (<div><span className="inline-block text-[10px] uppercase tracking-wider bg-primary/10 text-primary rounded-full px-2 py-0.5" data-testid="cc-view-only-badge">CC · View only</span></div>)}
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">#</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">VAT %</TableHead>
              <TableHead className="text-right">VAT</TableHead>
              <TableHead className="text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {v.line_items.map((l, i) => (
              <TableRow key={i}>
                <TableCell className="text-slate-400 font-mono">{i + 1}</TableCell>
                <TableCell>{l.description}</TableCell>
                <TableCell className="text-right font-mono">{formatMoney(l.amount, v.currency)}</TableCell>
                <TableCell className="text-right font-mono">{l.vat_rate}%</TableCell>
                <TableCell className="text-right font-mono text-emerald-600 dark:text-emerald-400">{formatMoney(l.vat_amount, v.currency)}</TableCell>
                <TableCell className="text-right font-mono font-medium">{formatMoney(l.line_total, v.currency)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-5 pt-5">
          <div className="max-w-sm" data-testid="amount-in-words">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Amount in Words</div>
            <div className="text-sm font-medium leading-snug text-slate-700 dark:text-slate-200">{amountToWords(v.grand_total, v.currency)}</div>
          </div>
          <div className="w-full sm:w-auto sm:min-w-[16rem] space-y-2">
            <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-mono">{formatMoney(v.subtotal, v.currency)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">VAT</span><span className="font-mono text-emerald-600 dark:text-emerald-400">{formatMoney(v.vat_total, v.currency)}</span></div>
            <div className="flex justify-between items-center bg-primary text-primary-foreground rounded-md px-3 py-2.5">
              <span className="font-semibold">Grand Total</span><span className="font-mono font-bold text-lg">{formatMoney(v.grand_total, v.currency)}</span>
            </div>
          </div>
        </div>

        {v.notes && (<div className="mt-5 text-sm border-t border-slate-200 dark:border-slate-800 pt-4"><span className="text-slate-400">Notes: </span>{v.notes}</div>)}
        {v.additional_info && (<div className="mt-3 text-sm whitespace-pre-wrap" data-testid="additional-info-display"><span className="text-slate-400">Additional Info: </span>{v.additional_info}</div>)}

        <div className="grid gap-6 mt-10 pt-4" style={{ gridTemplateColumns: `repeat(${signStages.length}, minmax(0, 1fr))` }} data-testid="signature-row">
          {signStages.map((s) => {
            const name = signerByStage[s] || (s === "draft" ? v.created_by_name : "");
            return (
              <div key={s} className="text-center" data-testid={`signature-${s}`}>
                <div className="h-6 flex items-end justify-center pb-1 text-sm font-medium truncate">{name || ""}</div>
                <div className="border-b border-slate-400" />
                <div className="text-xs uppercase tracking-wider text-slate-400 mt-1.5">{STEP_LABEL[s]}</div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Attachments */}
      <div className="no-print space-y-4">
        {attachSections.map((sec) => (
          <Card key={sec.kind} className="p-5 border-slate-200 dark:border-slate-800" data-testid={`attach-${sec.kind}`}>
            <div className="flex items-start justify-between mb-3 gap-2">
              <div>
                <h3 className="font-semibold text-base flex items-center gap-2"><Paperclip className="w-4 h-4" /> {sec.title}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{sec.hint}</p>
              </div>
              {sec.can && (
                <label className="cursor-pointer shrink-0">
                  <input type="file" className="hidden" data-testid={`upload-${sec.kind}`} disabled={busy}
                    onChange={(e) => { uploadFile(sec.kind, e.target.files); e.target.value = ""; }} />
                  <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                    <Upload className="w-4 h-4" /> Upload
                  </span>
                </label>
              )}
            </div>
            {sec.list.length === 0 ? (
              <div className="text-sm text-slate-400 py-3">No files.</div>
            ) : (
              <ul className="space-y-2">
                {sec.list.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-2 text-sm border border-slate-200 dark:border-slate-800 rounded-md px-3 py-2" data-testid={`att-${a.id}`}>
                    <button onClick={() => openAtt(a.id)} className="flex items-center gap-2 min-w-0 hover:underline text-left">
                      <FileText className="w-4 h-4 shrink-0 text-slate-400" />
                      <span className="truncate">{a.original_filename}</span>
                    </button>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-slate-400 font-mono">{Math.max(1, Math.round((a.size || 0) / 1024))} KB</span>
                      {sec.can && (
                        <button onClick={() => delAtt(sec.kind, a.id)} data-testid={`del-att-${a.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ))}
      </div>
      </div>

      {/* Right sidebar */}
      <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-20 self-start no-print" data-testid="voucher-sidebar">
        {/* Total amount summary */}
        <Card className="border-none p-6 bg-primary text-primary-foreground shadow-lg" data-testid="total-amount-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80">
            <BadgeDollarSign className="w-4 h-4" /> Total Amount
          </div>
          <div className="font-mono text-3xl font-bold mt-2 tracking-tight">{formatMoney(v.grand_total, v.currency)}</div>
          <div className="grid grid-cols-2 gap-4 mt-5 pt-4 border-t border-white/20">
            <div>
              <div className="text-[11px] uppercase tracking-wider opacity-70">Items</div>
              <div className="font-semibold mt-0.5">{v.line_items.length}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider opacity-70">Currency</div>
              <div className="font-semibold mt-0.5">{v.currency || "AED"}</div>
            </div>
          </div>
        </Card>

        {/* Activity Log */}
        <Card className="border-slate-200 dark:border-slate-800">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800">
          <h3 className="font-semibold text-base">Activity Log</h3>
        </div>
        <div className="p-5">
          {audit.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-500">No activity yet.</div>
          ) : (
            <ol className="relative">
              {audit.map((a, i) => {
                const { Icon, cls } = activityIcon(a.action);
                const last = i === audit.length - 1;
                return (
                  <li key={a.id} className="relative flex gap-4 pb-7 last:pb-0" data-testid={`activity-${a.id}`}>
                    {!last && <span className="absolute left-5 top-11 -bottom-0 w-px bg-slate-200 dark:bg-slate-700" />}
                    <div className={cn("w-10 h-10 rounded-full flex items-center justify-center shrink-0 z-10", cls)}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="pt-1 min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="font-semibold uppercase tracking-wide text-sm text-slate-900 dark:text-slate-100">{a.action}</span>
                        {a.user_name && <span className="text-sm text-slate-500">by {a.user_name}</span>}
                      </div>
                      <div className="text-sm text-slate-500 mt-0.5 font-mono">{formatDateTime(a.timestamp)}</div>
                      {a.notes && (
                        <div className="mt-2 text-sm bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 rounded-md px-3 py-2 text-slate-600 dark:text-slate-300">
                          {a.notes}
                        </div>
                      )}
                      {(a.from_status || a.to_status) && (
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          {a.from_status && <TinyPill s={a.from_status} />}
                          {a.from_status && a.to_status && <span className="text-slate-400">→</span>}
                          {a.to_status && <TinyPill s={a.to_status} />}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </Card>
      </aside>
      </div>

      {/* Unlock dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject voucher</DialogTitle>
            <DialogDescription>
              This returns the voucher to the <strong>Creator</strong> as a Draft. A reason is required and is logged in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Reason (required)</Label>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Missing supporting invoice / wrong amount" className="mt-1.5" data-testid="reject-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button className="gap-2 bg-rose-600 hover:bg-rose-500 text-white" onClick={doReject} disabled={busy} data-testid="confirm-reject-btn">
              <XCircle className="w-4 h-4" /> Reject to Creator
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={unlockOpen} onOpenChange={setUnlockOpen}>
        <DialogContent data-testid="unlock-dialog">
          <DialogHeader>
            <DialogTitle>Unlock paid voucher</DialogTitle>
            <DialogDescription>
              Unlocking reverts this voucher to <strong>Unpaid</strong> so it can be edited. This action is logged in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Reason (required)</Label>
            <Textarea value={unlockReason} onChange={(e) => setUnlockReason(e.target.value)}
              placeholder="e.g. Corrected VAT amount per finance review" className="mt-1.5" data-testid="unlock-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlockOpen(false)}>Cancel</Button>
            <Button className="gap-2" onClick={doUnlock} disabled={busy} data-testid="confirm-unlock-btn">
              <Unlock className="w-4 h-4" /> Unlock voucher
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent data-testid="request-info-dialog">
          <DialogHeader>
            <DialogTitle>Request additional info</DialogTitle>
            <DialogDescription>The voucher goes back to the creator. Once they submit the info it returns to this stage.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">What do you need? (required)</Label>
            <Textarea value={infoNote} onChange={(e) => setInfoNote(e.target.value)} className="mt-1.5"
              placeholder="e.g. Please confirm the delivery date and attach the signed delivery note" data-testid="request-info-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInfoOpen(false)}>Cancel</Button>
            <Button className="gap-2" onClick={doRequestInfo} disabled={busy} data-testid="confirm-request-info-btn">
              <MessageSquareMore className="w-4 h-4" /> Send to Creator
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="pay-dialog">
          <DialogHeader>
            <DialogTitle>Mark voucher as Paid</DialogTitle>
            <DialogDescription>The voucher will be locked after it is marked Paid. Details are saved in the activity log.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Payment date (optional)</Label>
              <Input type="date" value={payForm.payment_date} className="mt-1.5" data-testid="pay-date-input"
                onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Payment reference (optional)</Label>
              <Input value={payForm.payment_reference} placeholder="e.g. Bank transfer ref / cheque no." className="mt-1.5" data-testid="pay-ref-input"
                onChange={(e) => setPayForm({ ...payForm, payment_reference: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Note (optional)</Label>
              <Textarea value={payForm.notes} className="mt-1.5" data-testid="pay-note-input"
                onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button className="gap-2 bg-emerald-600 hover:bg-emerald-500 text-white" onClick={doPay} disabled={busy} data-testid="confirm-pay-btn">
              <BadgeDollarSign className="w-4 h-4" /> Mark as Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
